import { useState, useEffect, useRef, useMemo } from 'react';
import { X, Search } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { User } from '@bloom/shared';
import { Pagination } from '../common/Pagination';
import { useUser } from '../../store/useAuthStore';
import { useServerList } from '../../hooks/useServerList';

// =============================================================================
// PRICING CONTEXT TYPES FOR CATALOG FORM
// =============================================================================
// These types ensure we use the correct price field based on catalog mode:
// - Admin templates: use 'cost' which represents basePrice (admin's floor cost)
// - AM physician catalogs: use 'cost' which represents AM's cost from assigned catalogs
//
// By using a common 'cost' field name with different semantics based on context,
// we prevent accidentally using the wrong price. The 'pricingContext' field
// documents the source of the cost value.
// =============================================================================

/**
 * Product with pricing context for catalog forms
 * The 'cost' field represents the base cost for markup calculations:
 * - For admin templates: cost = basePrice (admin's floor cost)
 * - For AM catalogs: cost = price from assigned admin catalogs
 */
interface CatalogFormProduct {
  _id: string;
  drugName: string;
  concentration: string;
  format: string;
  size: string;
  displayName: string;
  /**
   * The cost used for markup calculations.
   * - Admin template mode: this is the basePrice (floor cost)
   * - AM catalog mode: this is the AM's cost from assigned admin catalogs
   */
  cost: number;
  /**
   * Documents where the cost value came from
   * - 'admin': cost is the raw basePrice
   * - 'am': cost is from assigned admin catalogs
   */
  pricingContext: 'admin' | 'am';
  /** For AM context, the catalog this cost came from */
  fromCatalog?: string;
  /**
   * Sales-rep display config, present only when this product comes from the
   * special sales-rep catalog. `cost` is the rep's floor; `salesRep.listPrice`
   * is the full sale price; base commission $ = listPrice − cost. `tierPercents`
   * is the global ladder of commission percentages the rep may give up; the UI
   * renders them as per-product dollar amounts.
   */
  salesRep?: {
    listPrice: number;
    tierPercents: number[];
  };
}

interface CatalogProduct {
  productId: string;
  price: number;
  notes?: string;
}

interface CatalogFormData {
  catalogName: string;
  description: string;
  products: CatalogProduct[];
  assignedTo: string[];
  tags: string[];
  isActive?: boolean;
  catalogType?: 'cost' | 'msrp';
}

type AssigneeSource = { role: 'supervisor' | 'distributor' | 'physician'; supervisorId?: string };

interface CatalogFormProps {
  mode: 'create-template' | 'edit-template' | 'create-physician-catalog' | 'edit-physician-catalog';
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: CatalogFormData;
  catalogId?: string;
  // For assignment management (physicians for AM catalogs, AMs for admin templates)
  enableAssignmentSelection?: boolean;
  selectedAssignees?: User[];
  onAssigneesChange?: (assignees: User[]) => void;
}

export const CatalogForm: React.FC<CatalogFormProps> = ({
  mode,
  isOpen,
  onClose,
  onSuccess,
  initialData,
  catalogId,
  enableAssignmentSelection = false,
  selectedAssignees = [],
  onAssigneesChange
}) => {
  const currentUser = useUser();
  const isSupervisor = currentUser?.role === 'supervisor';
  const isPercentMode = !!(currentUser as any)?.percentMarkupEnabled;
  const percentRate = (currentUser as any)?.percentMarkupPercent;
  // Internal sales-rep mode: the rep sells from the special catalog at its list
  // price and steps the price down in preset $ tiers, giving up commission. The
  // stored catalog price is still a plain markup price (listPrice − discount);
  // this only changes the catalog-builder UX.
  const isSalesRepMode = !!(currentUser as any)?.salesRepEnabled;

  const [formData, setFormData] = useState<CatalogFormData>({
    catalogName: '',
    description: '',
    products: [],
    assignedTo: [],
    tags: [],
    isActive: true,
    catalogType: 'cost'
  });
  const [tagInput, setTagInput] = useState('');

  // Full collection cached once per modal session. We derive what's
  // "available to add" client-side by filtering out formData.products,
  // applying the search box, and paginating — this avoids the stale
  // closure / pagination drift that plagued server-side excludeIds.
  const [allAvailableProducts, setAllAvailableProducts] = useState<CatalogFormProduct[]>([]);
  const [currentCatalogProducts, setCurrentCatalogProducts] = useState<CatalogFormProduct[]>([]); // Products already in the catalog
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  
  // Pagination state for assignees (7 per page)
  const ASSIGNEES_PER_PAGE = 7;
  const [selectedAssigneesPage, setSelectedAssigneesPage] = useState(1);
  const [selectedAssigneesSearch, setSelectedAssigneesSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Ref to prevent re-loading products on every state change
  const hasLoadedProductsRef = useRef(false);
  // Ref for scrolling to the error banner when a save fails
  const errorBannerRef = useRef<HTMLDivElement>(null);

  // Who a catalog can be assigned to, searched on the server one page at a
  // time. Loading everyone up front and filtering in the browser cut the list
  // at the server's page cap, so later AMs/physicians could not be found.
  // - admin templates: supervisors and AMs
  // - supervisors: their AMs, plus physicians attached directly to them (the
  //   supervisorId narrows /physicians to those; AM-network physicians are
  //   rejected by the catalog API)
  // - AMs: their physicians
  const supervisorSelfId = isSupervisor ? String((currentUser as any)?._id || (currentUser as any)?.id || '') : '';
  const assigneeSources: AssigneeSource[] = mode.includes('template')
    ? [{ role: 'supervisor' }, { role: 'distributor' }]
    : isSupervisor
      ? [{ role: 'distributor' }, ...(supervisorSelfId ? [{ role: 'physician' as const, supervisorId: supervisorSelfId }] : [])]
      : [{ role: 'physician' }];
  const assigneeList = useServerList<User, { sources: AssigneeSource[] }>({
    filters: { sources: assigneeSources },
    pageSize: ASSIGNEES_PER_PAGE,
    enabled: isOpen && enableAssignmentSelection && showAssigneeDropdown,
    // Each page shows that page of every source, so every record is reachable.
    fetchPage: async ({ page, pageSize, search, filters, signal }) => {
      const pages = await Promise.all(filters.sources.map(source =>
        apiClient.searchUsersPage({ ...source, page, pageSize, search, signal })));
      return {
        items: pages.flatMap(p => p.items),
        total: pages.every(p => p.total !== null) ? pages.reduce((sum, p) => sum + (p.total ?? 0), 0) : null,
        hasMore: pages.some(p => p.hasMore),
      };
    },
  });

  useEffect(() => {
    if (error && errorBannerRef.current) {
      errorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);
  
  // Sorting state for Products & Pricing table
  const [sortField, setSortField] = useState<'drugName' | 'price' | 'markup'>('drugName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Product search states
  const [currentProductsSearch, setCurrentProductsSearch] = useState('');
  const [availableProductsSearch, setAvailableProductsSearch] = useState('');

  // Bulk markup state
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [bulkMarkupPercent, setBulkMarkupPercent] = useState<string>('');
  const [roundingIncrement, setRoundingIncrement] = useState<number>(5);

  // Pagination state for current products (in catalog)
  const [currentProductsPage, setCurrentProductsPage] = useState(1);
  const CURRENT_PRODUCTS_PER_PAGE = 20;

  // Pagination state for available products
  const AVAILABLE_PER_PAGE = 20;
  const [availableProductsPage, setAvailableProductsPage] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Pull the full product collection once. The backend caps each page at
  // 100, so we walk pages until exhausted and cache the result for the
  // rest of the modal session. After this, filter/search/paginate live
  // entirely client-side via useMemo below.
  const loadAllAvailableProducts = async () => {
    try {
      setLoadingProducts(true);
      const fetched: CatalogFormProduct[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        if (mode.includes('template')) {
          const response = await apiClient.getProducts({ page, limit: 100 });
          if (response.success && response.data) {
            fetched.push(...(response.data.products || []).map((p: any) => ({
              _id: p._id,
              drugName: p.drugName,
              concentration: p.concentration,
              format: p.format,
              size: p.size,
              displayName: p.displayName,
              cost: p.basePrice,  // Admin context: cost = basePrice
              pricingContext: 'admin' as const,
            })));
            hasMore = response.data.pagination.hasNextPage;
            page++;
          } else {
            hasMore = false;
          }
        } else {
          const response = await apiClient.getMyProductCollection({ page, limit: 100 });
          if (response.success && response.data) {
            fetched.push(...response.data.products.map((p: any) => ({
              _id: p._id,
              drugName: p.drugName,
              concentration: p.concentration,
              format: p.format,
              size: p.size,
              displayName: p.displayName,
              cost: p.cost ?? 0,  // AM context: cost from assigned catalogs
              pricingContext: 'am' as const,
              fromCatalog: p.fromCatalog,
              ...(p.salesRep ? { salesRep: p.salesRep } : {}),
            })));
            hasMore = response.data.pagination.hasNextPage;
            page++;
          } else {
            hasMore = false;
          }
        }
      }

      setAllAvailableProducts(fetched);
    } catch (error) {
      console.error('Failed to load available products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Derived: products not already in the catalog, filtered by search.
  const filteredAvailableProducts = useMemo(() => {
    const inCatalog = new Set(formData.products.map(p => p.productId));
    const q = availableProductsSearch.trim().toLowerCase();
    return allAvailableProducts.filter(p => {
      if (inCatalog.has(p._id)) return false;
      if (!q) return true;
      return (
        (p.drugName || '').toLowerCase().includes(q) ||
        (p.displayName || '').toLowerCase().includes(q) ||
        (p.format || '').toLowerCase().includes(q) ||
        (p.concentration || '').toLowerCase().includes(q)
      );
    });
  }, [allAvailableProducts, formData.products, availableProductsSearch]);

  // Derived: current page slice of the filtered list. Page self-clamps
  // when the filtered total shrinks (e.g. after adding a product).
  const pagedAvailableProducts = useMemo(() => {
    const total = filteredAvailableProducts.length;
    const totalPages = Math.max(1, Math.ceil(total / AVAILABLE_PER_PAGE));
    const page = Math.min(availableProductsPage, totalPages);
    const start = (page - 1) * AVAILABLE_PER_PAGE;
    return {
      products: filteredAvailableProducts.slice(start, start + AVAILABLE_PER_PAGE),
      total,
      totalPages,
      page,
    };
  }, [filteredAvailableProducts, availableProductsPage]);

  /**
   * Fetches products by IDs with cost resolved server-side.
   * Single API call replaces the old two-call pattern (getProductsByIds + getMyProductCollection).
   * - For admin templates: cost = basePrice (server resolves)
   * - For AM/supervisor catalogs: cost = lowest price from assigned admin catalogs (server resolves)
   */
  const fetchProductsWithCost = async (productIds: string[]): Promise<CatalogFormProduct[]> => {
    const response = await apiClient.getProductsByIdsWithCost(productIds);
    if (!response.success || !response.data) {
      return [];
    }

    const isAmCatalog = mode === 'create-physician-catalog' || mode === 'edit-physician-catalog';

    return response.data.products.map((p: any): CatalogFormProduct => ({
      _id: p._id,
      drugName: p.drugName,
      concentration: p.concentration,
      format: p.format,
      size: p.size,
      displayName: p.displayName,
      cost: p.cost ?? 0,
      pricingContext: isAmCatalog ? 'am' : 'admin',
      fromCatalog: p.fromCatalog,
      ...(p.salesRep ? { salesRep: p.salesRep } : {}),
    }));
  };

  // Load current catalog products - FETCH BY IDS
  const loadCurrentCatalogProducts = async () => {
    if (formData.products.length === 0) {
      setCurrentCatalogProducts([]);
      return;
    }

    try {
      const productIds = formData.products.map(p => p.productId);
      const products = await fetchProductsWithCost(productIds);
      setCurrentCatalogProducts(products);
    } catch (error) {
      console.error('Failed to load current catalog products:', error);
    }
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset loaded flags
      hasLoadedProductsRef.current = false;
      
      // Initialize formData. The available-products cache is loaded by the
      // post-init effect below once formData has committed; filtering and
      // pagination are then derived from that cache via useMemo.
      if (initialData) {
        setFormData(initialData);
        // Eagerly load the catalog's current products using initialData
        // directly — no closure dependency, so it's safe here.
        if (initialData.products.length > 0) {
          const productIds = initialData.products.map(p => p.productId);
          fetchProductsWithCost(productIds).then(products => {
            setCurrentCatalogProducts(products);
          });
        }
      } else {
        setFormData({
          catalogName: '',
          description: '',
          products: [],
          assignedTo: [],
          tags: [],
          isActive: true
        });
      }
      setAvailableProductsPage(1);
      setAvailableProductsSearch('');
      
      setError('');
      assigneeList.setSearch('');
      setShowAssigneeDropdown(false);
      setTagInput('');
      // Reset pagination state
      assigneeList.setPage(1);
      setSelectedAssigneesPage(1);
      setSelectedAssigneesSearch('');
    } else {
      // Reset when modal closes
      setCurrentCatalogProducts([]);
    }
  }, [isOpen, initialData]); // Removed enableAssignmentSelection from dependencies

  // Pull the full collection once after formData is initialized. Filtering,
  // searching, and pagination are derived from this cache (no further
  // server round-trips needed while the modal is open).
  useEffect(() => {
    if (!isOpen || hasLoadedProductsRef.current) return;

    const isInitialized = formData.catalogName !== '' || formData.products.length > 0 || !initialData;
    if (isInitialized) {
      hasLoadedProductsRef.current = true;
      loadAllAvailableProducts();
      if (formData.products.length > 0) {
        loadCurrentCatalogProducts();
      }
    }
  }, [isOpen, formData]);

  // Reset to page 1 whenever the search query changes so users don't land
  // on an empty page when the filtered list shrinks.
  useEffect(() => {
    setAvailableProductsPage(1);
  }, [availableProductsSearch]);

  const getSelectedAssigneesPaginationInfo = () => {
    // Filter by search
    const filtered = selectedAssigneesSearch.trim() === '' 
      ? selectedAssignees 
      : selectedAssignees.filter(assignee => {
          const fullName = `${assignee.profile?.firstName} ${assignee.profile?.lastName}`;
          return fullName.toLowerCase().includes(selectedAssigneesSearch.toLowerCase()) ||
                 assignee.email.toLowerCase().includes(selectedAssigneesSearch.toLowerCase());
        });
    const total = filtered.length;
    const totalPages = Math.ceil(total / ASSIGNEES_PER_PAGE);
    const startIndex = (selectedAssigneesPage - 1) * ASSIGNEES_PER_PAGE;
    const endIndex = Math.min(startIndex + ASSIGNEES_PER_PAGE, total);
    const paginatedItems = filtered.slice(startIndex, endIndex);
    return { total, totalPages, startIndex, endIndex, paginatedItems, filtered };
  };

  // Validation — block save if any product row has a coverage/pricing error
  const catalogIssues = useMemo(() => {
    const issues: { productId: string; name: string; kind: 'missing_upstream' | 'below_upstream'; cost: number; price: number }[] = [];
    const productIndex = new Map(currentCatalogProducts.map(p => [p._id, p]));
    for (const row of formData.products) {
      const product = productIndex.get(row.productId);
      if (!product) continue;
      const missingUpstream = product.pricingContext === 'am' && (!product.fromCatalog || product.cost === 0);
      const belowUpstream = !missingUpstream && row.price < product.cost;
      if (missingUpstream) {
        issues.push({
          productId: row.productId,
          name: product.displayName || product.drugName,
          kind: 'missing_upstream',
          cost: product.cost,
          price: row.price
        });
      } else if (belowUpstream) {
        issues.push({
          productId: row.productId,
          name: product.displayName || product.drugName,
          kind: 'below_upstream',
          cost: product.cost,
          price: row.price
        });
      }
    }
    return issues;
  }, [currentCatalogProducts, formData.products]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (catalogIssues.length > 0) {
      setError(
        `Cannot save: ${catalogIssues.length} product${catalogIssues.length === 1 ? ' has' : 's have'} pricing errors. Fix or remove the flagged rows first.`
      );
      return;
    }

    setLoading(true);
    setError('');

    try {
      let response;

      const submitData = {
        ...formData,
        assignedTo: selectedAssignees.map(u => u._id)
      };
      

      
      if (mode === 'create-template' || mode === 'create-physician-catalog') {
        // Both admin and AM use the same create endpoint now - unified interface
        response = mode === 'create-template' 
          ? await apiClient.createTemplate(submitData)
          : await apiClient.createCatalog(submitData);
      } else if ((mode === 'edit-template' || mode === 'edit-physician-catalog') && catalogId) {
        // Both admin and AM use the same update endpoint
        response = await apiClient.updateCatalog(catalogId, submitData);
      }
      
      if (response?.success) {
        onSuccess();
        onClose();
      } else {
        setError(response?.error || 'Failed to save catalog');
      }
    } catch (error: any) {
      setError(error?.response?.data?.error || error?.message || 'Failed to save catalog');
    } finally {
      setLoading(false);
    }
  };

  // Parse the assignedTo error to find offending users in the current assignees list
  // so we can offer a one-click remove. Error text comes verbatim from the backend —
  // matching the format emitted at backend/src/routes/catalogs.ts:578.
  const offendingAssignees = useMemo(() => {
    if (!error || !error.includes('Offending AMs:')) return [];
    const emailList = error.split('Offending AMs:')[1] || '';
    const emails = emailList.split(',').map(s => s.trim()).filter(Boolean);
    return selectedAssignees.filter(u => emails.includes(u.email));
  }, [error, selectedAssignees]);

  const handleRemoveOffenders = () => {
    if (!onAssigneesChange) return;
    const offenderIds = new Set(offendingAssignees.map(u => u._id));
    onAssigneesChange(selectedAssignees.filter(u => !offenderIds.has(u._id)));
    setError('');
  };

  const toggleProduct = (productId: string) => {
    const existingProduct = formData.products.find(p => p.productId === productId);

    if (existingProduct) {
      // Remove product — useMemo over the cached collection refreshes the
      // available list automatically when formData.products changes.
      const newProducts = formData.products.filter(p => p.productId !== productId);
      setFormData(prev => ({ ...prev, products: newProducts }));

      if (newProducts.length > 0) {
        fetchProductsWithCost(newProducts.map(p => p.productId)).then(products => {
          setCurrentCatalogProducts(products);
        });
      } else {
        setCurrentCatalogProducts([]);
      }
    } else {
      // Add product with cost as default price.
      const product = allAvailableProducts.find(p => p._id === productId);
      if (product) {
        // Sales-rep default = full list price (zero discount, full commission).
        const defaultPrice = isSalesRepMode && product.salesRep ? product.salesRep.listPrice : product.cost;
        const newProducts = [...formData.products, { productId, price: defaultPrice }];
        setFormData(prev => ({ ...prev, products: newProducts }));

        fetchProductsWithCost(newProducts.map(p => p.productId)).then(products => {
          setCurrentCatalogProducts(products);
        });
      }
    }
  };

  const addAllProducts = () => {
    // Operates over the cached collection — only adds products not yet in
    // the catalog so existing markups stay intact.
    const inCatalog = new Set(formData.products.map(p => p.productId));
    const additions = allAvailableProducts.filter(p => !inCatalog.has(p._id));
    if (additions.length === 0) return;

    const newEntries = additions.map(p => ({
      productId: p._id,
      price: isSalesRepMode && p.salesRep ? p.salesRep.listPrice : p.cost,
    }));

    setFormData(prev => ({
      ...prev,
      products: [...prev.products, ...newEntries],
    }));
    setCurrentCatalogProducts(prev => [...prev, ...additions]);
  };

  const clearAllProducts = () => {
    setFormData(prev => ({
      ...prev,
      products: []
    }));
    setCurrentCatalogProducts([]);
  };

  const updateProductPrice = (productId: string, price: number) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p.productId === productId ? { ...p, price } : p
      )
    }));
  };

  const addTag = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleAssigneeSelect = (assignee: User) => {
    if (onAssigneesChange && !selectedAssignees.find(p => p._id === assignee._id)) {
      onAssigneesChange([...selectedAssignees, assignee]);
    }
    assigneeList.setSearch('');
    setShowAssigneeDropdown(false);
  };

  const handleRemoveAssignee = (assigneeId: string) => {
    if (onAssigneesChange) {
      const newAssignees = selectedAssignees.filter(p => p._id !== assigneeId);
      onAssigneesChange(newAssignees);
      
      // Adjust page if current page becomes empty
      const newTotalPages = Math.ceil(newAssignees.length / ASSIGNEES_PER_PAGE);
      if (selectedAssigneesPage > newTotalPages && newTotalPages > 0) {
        setSelectedAssigneesPage(newTotalPages);
      }
    }
  };

  const handleAvailableProductsPageChange = (page: number) => {
    setAvailableProductsPage(page);
  };

  const handleSort = (field: 'drugName' | 'price' | 'markup') => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedProducts = () => {
    const productsToSort = formData.products
      .map(catalogProduct => {
        const product = currentCatalogProducts.find(p => p._id === catalogProduct.productId);
        if (!product) return null;

        // Apply search filter
        if (currentProductsSearch.trim()) {
          const searchLower = currentProductsSearch.toLowerCase();
          const matches = product.drugName.toLowerCase().includes(searchLower) ||
                 product.displayName?.toLowerCase().includes(searchLower) ||
                 `${product.concentration} ${product.format} ${product.size}`.toLowerCase().includes(searchLower);
          if (!matches) return null;
        }

        const cost = product.cost;  // Use the context-appropriate cost
        const price = catalogProduct.price;
        const markupAmount = price - cost;

        return {
          catalogProduct,
          product,
          cost,
          price,
          markupAmount
        };
      })
      .filter(Boolean) as Array<{
        catalogProduct: CatalogProduct;
        product: CatalogFormProduct;
        cost: number;
        price: number;
        markupAmount: number;
      }>;

    return productsToSort.sort((a, b) => {
      let comparison = 0;

      if (sortField === 'drugName') {
        comparison = a.product.drugName.localeCompare(b.product.drugName);
      } else if (sortField === 'price') {
        comparison = a.price - b.price;
      } else if (sortField === 'markup') {
        comparison = a.markupAmount - b.markupAmount;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Get paginated current products
  const getPaginatedCurrentProducts = () => {
    const allSorted = getSortedProducts();
    const totalPages = Math.ceil(allSorted.length / CURRENT_PRODUCTS_PER_PAGE);
    const startIndex = (currentProductsPage - 1) * CURRENT_PRODUCTS_PER_PAGE;
    const paginatedProducts = allSorted.slice(startIndex, startIndex + CURRENT_PRODUCTS_PER_PAGE);

    return {
      products: paginatedProducts,
      totalCount: allSorted.length,
      totalPages,
      currentPage: currentProductsPage
    };
  };

  // Reset current products page when search changes or products list changes significantly
  useEffect(() => {
    setCurrentProductsPage(1);
  }, [currentProductsSearch]);

  // Adjust page if current page becomes empty after removing products
  useEffect(() => {
    const { totalPages } = getPaginatedCurrentProducts();
    if (currentProductsPage > totalPages && totalPages > 0) {
      setCurrentProductsPage(totalPages);
    }
  }, [formData.products.length]);

  // Clear selection when search changes (product set changes)
  useEffect(() => {
    setSelectedProductIds(new Set());
  }, [currentProductsSearch]);

  // Clean up selected IDs when products are removed
  useEffect(() => {
    setSelectedProductIds(prev => {
      const currentIds = new Set(formData.products.map(p => p.productId));
      const cleaned = new Set([...prev].filter(id => currentIds.has(id)));
      return cleaned.size === prev.size ? prev : cleaned;
    });
  }, [formData.products]);

  const toggleSelectProduct = (productId: string) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const selectAllProducts = () => {
    const allIds = getSortedProducts().map(p => p.catalogProduct.productId);
    setSelectedProductIds(new Set(allIds));
  };

  const clearSelection = () => {
    setSelectedProductIds(new Set());
  };

  const applyBulkMarkup = (targetIds?: Set<string>) => {
    const markup = parseFloat(bulkMarkupPercent);
    if (isNaN(markup) || markup < 0) return;
    const ids = targetIds || selectedProductIds;
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p => {
        if (!ids.has(p.productId)) return p;
        const product = currentCatalogProducts.find(cp => cp._id === p.productId);
        if (!product) return p;
        const rawPrice = product.cost * (1 + markup / 100);
        const rounded = Math.ceil(rawPrice / roundingIncrement) * roundingIncrement;
        return { ...p, price: Math.max(rounded, product.cost) };
      })
    }));
    setSelectedProductIds(new Set());
  };

  // Preview what the markup will produce for a given cost
  const previewMarkupPrice = (cost: number): number | null => {
    const markup = parseFloat(bulkMarkupPercent);
    if (isNaN(markup) || markup < 0) return null;
    const rawPrice = cost * (1 + markup / 100);
    const rounded = Math.ceil(rawPrice / roundingIncrement) * roundingIncrement;
    return Math.max(rounded, cost);
  };

  const getTitle = () => {
    const isEdit = mode.includes('edit');
    return isEdit ? 'Edit Catalog' : 'Create Catalog';
  };

  const getDescription = () => {
    const isEdit = mode.includes('edit');
    const isAdmin = mode.includes('template');

    if (isEdit) {
      return 'Edit the catalog configuration and pricing.';
    } else if (isAdmin) {
      return 'Create a new catalog with fixed prices that can be assigned to Account Managers.';
    } else if (isSupervisor) {
      return 'Create a new catalog with fixed prices for your account managers and direct physicians.';
    } else {
      return 'Create a new catalog with fixed prices for physicians in your network.';
    }
  };



  const getAssigneeLabel = () => {
    if (mode.includes('template')) return 'Supervisors & Account Managers';
    if (isSupervisor) return 'Account Managers & Direct Physicians';
    return 'Physicians';
  };

  const getAssigneeSearchPlaceholder = () => {
    if (mode.includes('template')) return 'Search supervisors or account managers by name or email...';
    if (isSupervisor) return 'Search account managers or direct physicians by name or email...';
    return 'Search physicians by name or email...';
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" onClick={(e) => e.stopPropagation()}>
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">{getTitle()}</h3>
                  <p className="mt-1 text-sm text-gray-500">{getDescription()}</p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div ref={errorBannerRef} className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-sm text-red-800 whitespace-pre-wrap">{error}</div>
                  {offendingAssignees.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-red-200">
                      <div className="text-xs text-red-700 mb-2">
                        These {offendingAssignees.length} assignee{offendingAssignees.length === 1 ? '' : 's'} have supervisors and should be removed from this admin catalog (their pricing comes through the supervisor chain instead):
                      </div>
                      <ul className="text-xs text-red-700 list-disc ml-5 mb-3">
                        {offendingAssignees.map(u => (
                          <li key={u._id}>{u.email}</li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={handleRemoveOffenders}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                      >
                        Remove these {offendingAssignees.length} assignee{offendingAssignees.length === 1 ? '' : 's'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Catalog Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.catalogName}
                    onChange={(e) => setFormData({...formData, catalogName: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={mode.includes('template') ? "e.g., Standard Catalog, Premium Catalog" : "e.g., Dr. Smith's Catalog"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={mode.includes('template') ? "Optional description of this template catalog" : "Optional description of this catalog"}
                  />
                </div>

                {/* Catalog Type (admin templates only) */}
                {mode.includes('template') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Catalog Type</label>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="catalogType"
                          value="cost"
                          checked={formData.catalogType !== 'msrp'}
                          onChange={() => setFormData({...formData, catalogType: 'cost'})}
                          className="h-4 w-4 text-blue-600 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Cost Catalog</span>
                        <span className="ml-1 text-xs text-gray-500">(sets AM cost basis)</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="catalogType"
                          value="msrp"
                          checked={formData.catalogType === 'msrp'}
                          onChange={() => setFormData({...formData, catalogType: 'msrp'})}
                          className="h-4 w-4 text-blue-600 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">MSRP Template</span>
                        <span className="ml-1 text-xs text-gray-500">(suggested retail prices)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tags</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag(tagInput);
                        }
                      }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Type a tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={() => addTag(tagInput)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 inline-flex items-center justify-center w-4 h-4 text-blue-400 hover:text-blue-600"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>



                {/* Assignment Selection (physicians for AM catalogs, AMs for admin templates) */}
                {enableAssignmentSelection && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Search and Add {getAssigneeLabel()}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder={getAssigneeSearchPlaceholder()}
                        value={assigneeList.search}
                        onChange={(e) => assigneeList.setSearch(e.target.value)}
                        onFocus={() => setShowAssigneeDropdown(true)}
                        onBlur={() => {
                          // Delay hiding to allow clicking on dropdown items
                          setTimeout(() => setShowAssigneeDropdown(false), 150);
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      
                      {/* Dropdown with Pagination */}
                      {showAssigneeDropdown && assigneeList.items.length > 0 && (() => {
                        const { items: paginatedItems, total, page: dropdownPage, hasMore, setPage: setDropdownPage } = assigneeList;
                        return (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                            {/* Dropdown Items */}
                            <div className="max-h-[280px] overflow-y-auto">
                              {paginatedItems.map(assignee => (
                                <div
                                  key={assignee._id}
                                  onClick={() => handleAssigneeSelect(assignee)}
                                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-sm">
                                    {assignee.profile?.firstName} {assignee.profile?.lastName}
                                  </div>
                                  <div className="text-xs text-gray-500">{assignee.email}</div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Pagination Controls */}
                            {(dropdownPage > 1 || hasMore) && (
                              <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs">
                                <span className="text-gray-600">
                                  {total !== null ? `${total} matches` : ''}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (dropdownPage > 1) setDropdownPage(dropdownPage - 1);
                                    }}
                                    disabled={dropdownPage === 1}
                                    className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Prev
                                  </button>
                                  <span className="text-gray-600">
                                    Page {dropdownPage}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (hasMore) setDropdownPage(dropdownPage + 1);
                                    }}
                                    disabled={!hasMore}
                                    className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Next
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* No results message */}
                      {showAssigneeDropdown && assigneeList.search.trim() && !assigneeList.loading && assigneeList.items.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                          <div className="px-4 py-3 text-gray-500 text-sm">
                            No {getAssigneeLabel().toLowerCase()} found matching "{assigneeList.search}"
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Selected Assignees List with Search & Pagination */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Assigned {getAssigneeLabel()} ({selectedAssignees.length})
                      </label>
                      {selectedAssignees.length === 0 ? (
                        <div className="text-center py-4 text-gray-500 border border-gray-200 rounded-md">
                          No {getAssigneeLabel().toLowerCase()} selected
                        </div>
                      ) : (() => {
                        const { total, totalPages, paginatedItems, startIndex, endIndex } = getSelectedAssigneesPaginationInfo();
                        return (
                          <div className="border border-gray-200 rounded-md overflow-hidden">
                            {/* Search Bar for Selected Assignees */}
                            {selectedAssignees.length > ASSIGNEES_PER_PAGE && (
                              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder={`Search assigned ${getAssigneeLabel().toLowerCase()}...`}
                                    value={selectedAssigneesSearch}
                                    onChange={(e) => {
                                      setSelectedAssigneesSearch(e.target.value);
                                      setSelectedAssigneesPage(1); // Reset to page 1 on search
                                    }}
                                    className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>
                            )}
                            
                            {/* Header */}
                            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                              <div className="grid grid-cols-10 gap-4 text-xs font-medium text-gray-700 uppercase tracking-wide">
                                <div className="col-span-8">{getAssigneeLabel()}</div>
                                <div className="col-span-2">Actions</div>
                              </div>
                            </div>
                            
                            {/* List Items */}
                            {paginatedItems.length === 0 && selectedAssigneesSearch.trim() ? (
                              <div className="text-center py-4 text-gray-500">
                                No {getAssigneeLabel().toLowerCase()} match "{selectedAssigneesSearch}"
                              </div>
                            ) : (
                              paginatedItems.map((assignee) => (
                                <div key={assignee._id} className="px-3 py-2 border-b border-gray-200 last:border-b-0">
                                  <div className="grid grid-cols-10 gap-4 items-center">
                                    <div className="col-span-8">
                                      <div className="font-medium text-sm">
                                        {assignee.profile?.firstName} {assignee.profile?.lastName}
                                      </div>
                                      <div className="text-xs text-gray-500">{assignee.email}</div>
                                    </div>
                                    
                                    <div className="col-span-2">
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveAssignee(assignee._id)}
                                        className="text-red-600 hover:text-red-800 font-medium text-sm"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                            
                            {/* Pagination Footer */}
                            {totalPages > 1 && (
                              <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs">
                                <span className="text-gray-600">
                                  Showing {startIndex + 1}-{endIndex} of {total}
                                  {selectedAssigneesSearch.trim() && total !== selectedAssignees.length && (
                                    <span> (filtered from {selectedAssignees.length})</span>
                                  )}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (selectedAssigneesPage > 1) setSelectedAssigneesPage(selectedAssigneesPage - 1);
                                    }}
                                    disabled={selectedAssigneesPage === 1}
                                    className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Prev
                                  </button>
                                  <span className="text-gray-600">
                                    Page {selectedAssigneesPage} of {totalPages}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (selectedAssigneesPage < totalPages) setSelectedAssigneesPage(selectedAssigneesPage + 1);
                                    }}
                                    disabled={selectedAssigneesPage === totalPages}
                                    className="px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Next
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Products & Pricing Section - Currently Selected Products */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      Products & Pricing
                      {(() => {
                        const { totalCount } = getPaginatedCurrentProducts();
                        if (currentProductsSearch.trim()) {
                          return (
                            <span className="text-gray-600">
                              {' '}({totalCount} of {formData.products.length} matching)
                            </span>
                          );
                        }
                        return <span className="text-gray-600"> ({formData.products.length})</span>;
                      })()}
                    </h4>
                    <div className="space-x-2">
                      <button
                        type="button"
                        onClick={addAllProducts}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Add All
                      </button>
                      <button
                        type="button"
                        onClick={clearAllProducts}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {formData.products.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 border border-gray-200 rounded-md">
                      No products selected
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Search Input for Current Products */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search current products by name..."
                          value={currentProductsSearch}
                          onChange={(e) => setCurrentProductsSearch(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Apply % Markup Toolbar — hidden in pass-through (percent) and sales-rep modes */}
                      {!isPercentMode && !isSalesRepMode && (
                      <div className={`flex flex-wrap items-center gap-3 ${selectedProductIds.size > 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'} border rounded-md px-3 py-2 text-sm`}>
                        <span className={`font-medium ${selectedProductIds.size > 0 ? 'text-blue-800' : 'text-gray-600'}`}>
                          Apply % Markup{selectedProductIds.size > 0 ? ` (${selectedProductIds.size} selected)` : ''}
                        </span>
                        <div className="flex flex-wrap items-center gap-2 ml-auto">
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={bulkMarkupPercent}
                            onChange={(e) => setBulkMarkupPercent(e.target.value)}
                            placeholder="e.g. 20"
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <span className="text-gray-500">%</span>
                          <label className="text-gray-700">Round to:</label>
                          <select
                            value={roundingIncrement}
                            onChange={(e) => setRoundingIncrement(Number(e.target.value))}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            <option value={1}>$1</option>
                            <option value={5}>$5</option>
                            <option value={10}>$10</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              if (selectedProductIds.size === 0) {
                                const allIds = new Set(formData.products.map(p => p.productId));
                                applyBulkMarkup(allIds);
                              } else {
                                applyBulkMarkup();
                              }
                            }}
                            disabled={!bulkMarkupPercent || parseFloat(bulkMarkupPercent) < 0 || formData.products.length === 0}
                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            {selectedProductIds.size > 0 ? 'Apply to Selected' : 'Apply to All'}
                          </button>
                          {selectedProductIds.size > 0 && (
                            <button
                              type="button"
                              onClick={clearSelection}
                              className="px-3 py-1 text-gray-600 hover:text-gray-800 text-sm"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                      )}

                      {isPercentMode && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-800">
                          <strong>Pass-through pricing mode.</strong> Catalog prices match upstream automatically.
                          Your earnings: a flat <strong>{percentRate ?? 0}%</strong> of each sale.
                          You control which products are included and who they're assigned to.
                        </div>
                      )}

                      {isSalesRepMode && (
                        <div className="bg-purple-50 border border-purple-200 rounded-md px-3 py-2 text-sm text-purple-800">
                          <strong>Sales-rep pricing.</strong> Each product sells at its sale price. Step the price
                          down with a discount tier to win the sale — every dollar you discount comes out of your
                          commission. Pick a tier per product below.
                        </div>
                      )}

                      <div className="border border-gray-200 rounded-md">
                        {/* Desktop Header - hidden on mobile */}
                        <div className="hidden md:block bg-gray-50 px-3 py-2 border-b border-gray-200">
                          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-700 uppercase tracking-wide">
                            <div className="col-span-1 flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedProductIds.size > 0 && selectedProductIds.size === getSortedProducts().length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    selectAllProducts();
                                  } else {
                                    clearSelection();
                                  }
                                }}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleSort('drugName')}
                              className="col-span-3 text-left hover:text-gray-900 flex items-center gap-1"
                            >
                              Product
                              {sortField === 'drugName' && (
                                <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                              )}
                            </button>
                            <div className="col-span-2">{isSalesRepMode ? 'Sale Price' : 'Your Cost'}</div>
                            <button
                              type="button"
                              onClick={() => handleSort('price')}
                              className="col-span-2 text-left hover:text-gray-900 flex items-center gap-1"
                            >
                              {isSalesRepMode ? 'Discount' : 'Your Price'}
                              {sortField === 'price' && (
                                <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSort('markup')}
                              className="col-span-2 text-left hover:text-gray-900 flex items-center gap-1"
                            >
                              {isSalesRepMode ? 'Commission' : 'Markup'}
                              {sortField === 'markup' && (
                                <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                              )}
                            </button>
                            <div className="col-span-2">Actions</div>
                          </div>
                        </div>
                        
                        <div className="divide-y divide-gray-200 max-h-96 overflow-y-scroll">
                          {getPaginatedCurrentProducts().products.map(({ catalogProduct, product, cost, price, markupAmount }) => {
                          const markupPercentage = cost > 0 ? ((markupAmount / cost) * 100) : 0;
                          const missingUpstream = product.pricingContext === 'am' && (!product.fromCatalog || cost === 0);
                          const belowUpstream = !missingUpstream && markupAmount < 0;
                          // Sales-rep discount tiers: global commission-% ladder → per-product
                          // dollar amounts (always offer 0 = full commission). repBase is the
                          // whole-dollar base commission. The discount leaves the rep's retained
                          // commission rounded UP to the nearest dollar, so the deepest tier still
                          // nets at least $1 (never $0 or cents).
                          const repBase = product.salesRep ? Math.round(product.salesRep.listPrice - cost) : 0;
                          const repTierDollar = (pct: number) => repBase - Math.ceil(repBase * (1 - pct / 100));
                          const repPcts = product.salesRep
                            ? Array.from(new Set([0, ...(product.salesRep.tierPercents || [])])).sort((a, b) => a - b)
                            : [];
                          const repCurrentDiscount = product.salesRep ? product.salesRep.listPrice - price : 0;
                          const repSelectedPct = product.salesRep
                            ? (repPcts.find(p => Math.abs(repTierDollar(p) - repCurrentDiscount) < 0.005) ?? 0)
                            : 0;

                          return (
                            <div key={product._id} className={`p-3 hover:bg-gray-50 ${missingUpstream || belowUpstream ? 'bg-red-50' : ''}`}>
                              {/* Desktop Layout */}
                              <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-1 flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedProductIds.has(catalogProduct.productId)}
                                    onChange={() => toggleSelectProduct(catalogProduct.productId)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                  />
                                </div>
                                <div className="col-span-3">
                                  <div className="font-medium text-sm">{product.drugName}</div>
                                  <div className="text-xs text-gray-500">{product.concentration} {product.format} {product.size}</div>
                                </div>
                                
                                <div className="col-span-2">
                                  {isSalesRepMode && product.salesRep ? (
                                    <>
                                      <div className="font-medium text-gray-900">${product.salesRep.listPrice.toFixed(2)}</div>
                                      <div className="text-xs text-gray-500">Sale price</div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="font-medium text-gray-900">${cost.toFixed(2)}</div>
                                      <div className="text-xs text-gray-500">What you pay</div>
                                    </>
                                  )}
                                </div>

                                <div className="col-span-2">
                                  {isSalesRepMode && product.salesRep ? (
                                    <div className="flex flex-col space-y-1">
                                      <select
                                        value={repSelectedPct}
                                        onChange={(e) => updateProductPrice(catalogProduct.productId, product.salesRep!.listPrice - repTierDollar(parseFloat(e.target.value)))}
                                        className="w-full text-sm border border-gray-300 rounded px-2 py-2 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                      >
                                        {repPcts.map((pct) => (
                                          <option key={pct} value={pct}>
                                            ${repTierDollar(pct).toFixed(2)} off{pct > 0 ? ` (${pct}%)` : ''}
                                          </option>
                                        ))}
                                      </select>
                                      <div className="text-xs text-gray-500">Physician pays ${price.toFixed(2)}</div>
                                    </div>
                                  ) : (
                                  <div className="flex flex-col space-y-1">
                                    <div className="relative">
                                      <span className="absolute left-2 top-2 text-sm text-gray-600">$</span>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        defaultValue={price.toFixed(2)}
                                        key={`desktop-${catalogProduct.productId}-${price}`}
                                        readOnly={isPercentMode}
                                        disabled={isPercentMode}
                                        onBlur={(e) => {
                                          if (isPercentMode) return;
                                          const value = parseFloat(e.target.value);
                                          if (isNaN(value) || value < cost) {
                                            updateProductPrice(catalogProduct.productId, cost);
                                            e.target.value = cost.toFixed(2);
                                          } else {
                                            updateProductPrice(catalogProduct.productId, value);
                                            e.target.value = value.toFixed(2);
                                          }
                                        }}
                                        className={`w-full text-sm border rounded pl-6 pr-2 py-2 focus:outline-none focus:ring-1 ${
                                          isPercentMode
                                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200'
                                            : price < cost
                                              ? 'border-red-300 focus:ring-red-500'
                                              : 'border-gray-300 focus:ring-blue-500'
                                        }`}
                                        placeholder={cost.toFixed(2)}
                                      />
                                    </div>
                                    {(() => {
                                      const preview = previewMarkupPrice(cost);
                                      const isSelected = selectedProductIds.has(catalogProduct.productId) || selectedProductIds.size === 0;
                                      return preview !== null && isSelected && preview !== price ? (
                                        <div className="text-xs text-blue-600">
                                          → ${preview.toFixed(2)} after markup
                                        </div>
                                      ) : (
                                        <div className="text-xs text-gray-500">What you charge</div>
                                      );
                                    })()}
                                    {price < cost && (
                                      <div className="text-xs text-red-600">
                                        Price cannot be below cost (${cost.toFixed(2)})
                                      </div>
                                    )}
                                  </div>
                                  )}
                                </div>

                                <div className="col-span-2">
                                  <div className="text-sm">
                                    <div className={`font-medium ${markupAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {markupAmount >= 0 ? '+' : ''}${markupAmount.toFixed(2)}
                                    </div>
                                    {isSalesRepMode && product.salesRep ? (
                                      <div className="text-xs text-gray-500">
                                        Your commission{(product.salesRep.listPrice - cost) > markupAmount + 0.005 ? ` (was $${(product.salesRep.listPrice - cost).toFixed(2)})` : ''}
                                      </div>
                                    ) : (
                                    <div className={`text-xs ${markupAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {markupPercentage >= 0 ? '+' : ''}{markupPercentage.toFixed(1)}%
                                    </div>
                                    )}
                                    {missingUpstream && (
                                      <div
                                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200"
                                        title="No admin/supervisor catalog carries this product for you. Orders silently fall back to basePrice — the margin the pharmacy pays you may be wrong."
                                      >
                                        ⚠ No upstream
                                      </div>
                                    )}
                                    {belowUpstream && (
                                      <div
                                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200"
                                        title={`This price ($${price.toFixed(2)}) is below your upstream cost ($${cost.toFixed(2)}). Orders will fail with a pricing hierarchy error until the price is raised to at least $${cost.toFixed(2)}.`}
                                      >
                                        ⚠ Below upstream
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="col-span-2">
                                  <button
                                    type="button"
                                    onClick={() => toggleProduct(product._id)}
                                    className="text-red-600 hover:text-red-800 font-medium text-sm"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>

                              {/* Mobile Layout */}
                              <div className="md:hidden space-y-3">
                                <div className="flex justify-between items-start gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedProductIds.has(catalogProduct.productId)}
                                    onChange={() => toggleSelectProduct(catalogProduct.productId)}
                                    className="h-4 w-4 mt-1 text-blue-600 border-gray-300 rounded flex-shrink-0"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{product.drugName}</div>
                                    <div className="text-xs text-gray-500">{product.concentration} {product.format} {product.size}</div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => toggleProduct(product._id)}
                                    className="text-red-600 hover:text-red-800 font-medium text-sm ml-2"
                                  >
                                    Remove
                                  </button>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500 mb-1">{isSalesRepMode && product.salesRep ? 'Sale Price' : 'Your Cost'}</div>
                                    <div className="font-medium text-gray-900">${(isSalesRepMode && product.salesRep ? product.salesRep.listPrice : cost).toFixed(2)}</div>
                                  </div>

                                  <div>
                                    <div className="text-xs text-gray-500 mb-1">{isSalesRepMode && product.salesRep ? 'Discount' : 'Your Price'}</div>
                                    {isSalesRepMode && product.salesRep ? (
                                      <>
                                        <select
                                          value={repSelectedPct}
                                          onChange={(e) => updateProductPrice(catalogProduct.productId, product.salesRep!.listPrice - repTierDollar(parseFloat(e.target.value)))}
                                          className="w-full text-sm border border-gray-300 rounded px-2 py-2 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                        >
                                          {repPcts.map((pct) => (
                                            <option key={pct} value={pct}>
                                              ${repTierDollar(pct).toFixed(2)} off{pct > 0 ? ` (${pct}%)` : ''}
                                            </option>
                                          ))}
                                        </select>
                                        <div className="text-xs text-gray-500 mt-1">Physician pays ${price.toFixed(2)}</div>
                                      </>
                                    ) : (
                                    <>
                                    <div className="relative">
                                      <span className="absolute left-2 top-2 text-sm text-gray-600">$</span>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        defaultValue={price.toFixed(2)}
                                        key={`mobile-${catalogProduct.productId}-${price}`}
                                        readOnly={isPercentMode}
                                        disabled={isPercentMode}
                                        onBlur={(e) => {
                                          if (isPercentMode) return;
                                          const value = parseFloat(e.target.value);
                                          if (isNaN(value) || value < cost) {
                                            updateProductPrice(catalogProduct.productId, cost);
                                            e.target.value = cost.toFixed(2);
                                          } else {
                                            updateProductPrice(catalogProduct.productId, value);
                                            e.target.value = value.toFixed(2);
                                          }
                                        }}
                                        className={`w-full text-sm border rounded pl-6 pr-2 py-2 focus:outline-none focus:ring-1 ${
                                          isPercentMode
                                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200'
                                            : price < cost
                                              ? 'border-red-300 focus:ring-red-500'
                                              : 'border-gray-300 focus:ring-blue-500'
                                        }`}
                                        placeholder={cost.toFixed(2)}
                                      />
                                    </div>
                                    {price < cost && (
                                      <div className="text-xs text-red-600 mt-1">
                                        Price cannot be below cost
                                      </div>
                                    )}
                                    </>
                                    )}
                                  </div>
                                </div>

                                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                  <div className="text-xs text-gray-500">{isSalesRepMode && product.salesRep ? 'Commission:' : 'Markup:'}</div>
                                  <div className="text-sm">
                                    <span className={`font-medium ${markupAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {markupAmount >= 0 ? '+' : ''}${markupAmount.toFixed(2)}
                                    </span>
                                    {isSalesRepMode && product.salesRep ? (
                                      (product.salesRep.listPrice - cost) > markupAmount + 0.005 ? (
                                        <span className="ml-2 text-xs text-gray-500">(was ${(product.salesRep.listPrice - cost).toFixed(2)})</span>
                                      ) : null
                                    ) : (
                                    <span className={`ml-2 text-xs ${markupAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      ({markupPercentage >= 0 ? '+' : ''}{markupPercentage.toFixed(1)}%)
                                    </span>
                                    )}
                                  </div>
                                </div>
                                {(missingUpstream || belowUpstream) && (
                                  <div className="flex flex-wrap gap-1 pt-1">
                                    {missingUpstream && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                        ⚠ No upstream
                                      </span>
                                    )}
                                    {belowUpstream && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                        ⚠ Below upstream (${cost.toFixed(2)})
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                          })}
                          
                          {/* No Results Message for Current Products */}
                          {formData.products.length > 0 &&
                           currentCatalogProducts.length > 0 &&
                           getPaginatedCurrentProducts().totalCount === 0 &&
                           currentProductsSearch.trim() && (
                            <div className="text-center py-8 text-gray-500">
                              No products found matching "{currentProductsSearch}"
                            </div>
                          )}
                        </div>

                        {/* Pagination for Current Products */}
                        {getPaginatedCurrentProducts().totalPages > 1 && (
                          <Pagination
                            currentPage={currentProductsPage}
                            totalPages={getPaginatedCurrentProducts().totalPages}
                            onPageChange={setCurrentProductsPage}
                            totalItems={getPaginatedCurrentProducts().totalCount}
                            itemsPerPage={CURRENT_PRODUCTS_PER_PAGE}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Available Products to Add Section */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      Available Products to Add
                      {pagedAvailableProducts.total > 0 && (
                        <span className="text-gray-600 ml-2">
                          ({pagedAvailableProducts.total} available)
                        </span>
                      )}
                    </h4>
                    {loadingProducts && (
                      <span className="text-sm text-gray-500">Loading...</span>
                    )}
                  </div>

                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products across all pages..."
                      value={availableProductsSearch}
                      onChange={(e) => setAvailableProductsSearch(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="border border-gray-200 rounded-md">
                    <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                      {loadingProducts ? (
                        <div className="text-center py-8 text-gray-500">
                          Loading products...
                        </div>
                      ) : pagedAvailableProducts.products.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          {availableProductsSearch.trim()
                            ? `No products found matching "${availableProductsSearch}"`
                            : 'No products available'}
                        </div>
                      ) : (
                        pagedAvailableProducts.products.map(product => (
                          <div key={product._id} className="p-3 hover:bg-gray-50 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{product.drugName}</div>
                              <div className="text-xs text-gray-500">
                                {product.concentration} {product.format} {product.size} - {product.pricingContext === 'admin' ? 'Base' : 'Cost'}: ${(product.cost ?? 0).toFixed(2)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleProduct(product._id)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm ml-4"
                            >
                              Add
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Pagination */}
                    {pagedAvailableProducts.totalPages > 1 && (
                      <Pagination
                        currentPage={pagedAvailableProducts.page}
                        totalPages={pagedAvailableProducts.totalPages}
                        onPageChange={handleAvailableProductsPageChange}
                        totalItems={pagedAvailableProducts.total}
                        itemsPerPage={AVAILABLE_PER_PAGE}
                      />
                    )}
                  </div>
                </div>

                {/* Active Catalog Checkbox - only for edit modes */}
                {mode.includes('edit') && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="catalogIsActive"
                      checked={formData.isActive !== false}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="catalogIsActive" className="ml-2 block text-sm text-gray-900">
                      Active catalog
                    </label>
                  </div>
                )}
              </div>
            </div>

            {catalogIssues.length > 0 && (
              <div className="bg-red-50 border-t border-red-200 px-4 py-3 sm:px-6">
                <div className="text-sm font-medium text-red-800">
                  ⚠ {catalogIssues.length} product{catalogIssues.length === 1 ? '' : 's'} block saving — fix or remove before saving
                </div>
                <ul className="mt-1 text-xs text-red-700 list-disc ml-5">
                  {catalogIssues.slice(0, 5).map(i => (
                    <li key={i.productId}>
                      <strong>{i.name}</strong>
                      {i.kind === 'missing_upstream'
                        ? ' — no upstream catalog carries this product'
                        : ` — priced at $${i.price.toFixed(2)}, upstream cost is $${i.cost.toFixed(2)}`}
                    </li>
                  ))}
                  {catalogIssues.length > 5 && (
                    <li>…and {catalogIssues.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading || catalogIssues.length > 0}
                title={catalogIssues.length > 0 ? `${catalogIssues.length} product(s) have pricing errors — fix before saving` : undefined}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 
                  mode.includes('create') ? 
                    (enableAssignmentSelection && selectedAssignees.length === 0 ? 
                      `Create ${mode.includes('template') ? 'Template' : 'Catalog'} (No ${getAssigneeLabel()})` : 
                      mode.includes('template') ? 'Create Template' : `Create Catalog for ${selectedAssignees.length} ${getAssigneeLabel().slice(0, -1)}${selectedAssignees.length !== 1 ? 's' : ''}`) :
                    'Save Changes'
                }
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

